import { createFormatForDescription as cffd } from '../createFormatForDescription'
import { MemoExplanation, createMemoExplanation } from '../explain/ExplainMemo'
import {
  MetadataExplanation,
  createMetadataExplanation,
} from '../explain/ExplainMetadata'
import {
  MultiSignatureExplanation,
  createMultiSignatureExplanation,
} from '../explain/ExplainMultiSignature'
import {
  Explanation,
  explain,
  explainOptional,
  explainOptionalArray,
} from '../explain/Explanation'
import { Flag, matchFlags, MatchFlag } from '../flag'
import Transaction from '../types/Transaction'
import TransactionType from '../types/TransactionType'
import { numberToRankString } from '../util'

/**
 * The base annotation for all annotations.
 *
 * @property type - The type of the transaction.
 *
 * @property date - An explanation of the date.
 * @property hash - An explanation of the hash.
 * @property ledgerHash - An explanation of the ledger_hash.
 * @property ledgerIndex - An explanation of the ledger_index.
 * @property meta - An explanation of the meta.
 * @property validated - An explanation of the validated.
 *
 * @property account - An explanation of the Account.
 * @property accountTxnID - An explanation of the AccountTxnID.
 * @property fee - An explanation of the Fee.
 * @property flags - An explanation of the Flags.
 * @property lastLedgerSequence - An explanation of the LastLedgerSequence.
 * @property memos - An explanation of the Memos.
 * @property sequence - An explanation of the Sequence.
 * @property signers - An explanation of the Signers.
 * @property signingPubKey - An explanation of the SigningPubKey.
 * @property sourceTag - An explanation of the SourceTag.
 * @property transactionType - An explanation of the TransactionType.
 * @property txnSignature - An explanation of the TxnSignature.
 */
export interface BaseAnnotation {
  readonly type: TransactionType

  readonly date?: Explanation<string>
  readonly hash: Explanation<string>
  readonly ledgerHash?: Explanation<string>
  readonly ledgerIndex?: Explanation<number>
  readonly meta: MetadataExplanation
  readonly validated: Explanation<boolean>

  readonly account: Explanation<string>
  readonly accountTxnID?: Explanation<string>
  readonly fee: Explanation<string>
  readonly flags: Explanation<Flag[]>
  readonly lastLedgerSequence?: Explanation<number>
  readonly memos?: Explanation<MemoExplanation[]>
  readonly sequence: Explanation<number>
  readonly signers?: Explanation<MultiSignatureExplanation[]>
  readonly signingPubKey: Explanation<string>
  readonly sourceTag?: Explanation<number>
  readonly transactionType: Explanation<string>
  readonly txnSignature: Explanation<string>
}

const baseFlags: MatchFlag[] = [
  {
    code: 0x80000000,
    name: 'tfFullyCanonicalSig',
    label: 'This transaction requires a fully-canonical signature.',
  },
]

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param txn - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
// eslint-disable-next-line complexity -- Cannot avoid this complexity.
export function annotate(txn: Transaction): BaseAnnotation {
  const hash = explain(
    txn.hash,
    `The hash of the transaction was ${cffd(txn.hash, 'hash')}.`,
  )

  const date = explainOptional(
    txn.date,
    (theDate) =>
      `The transaction was published in a ledger on ${cffd(
        new Date(Date.parse(theDate)).toUTCString(),
        'date',
      )}.`,
  )

  const ledgerIndex = explainOptional(
    txn.ledger_index,
    (index) =>
      `The transaction occurred in the ${cffd(
        numberToRankString(index),
        '',
      )} ledger.`,
  )

  const ledgerHash = explainOptional(
    txn.ledger_hash,
    (theHash) =>
      `The hash of the ledger containing this transaction was ${cffd(
        theHash,
        'ledgerHash',
      )}`,
  )

  const meta = createMetadataExplanation(txn.meta)

  const { tx: transactionInstructions } = txn

  const validated = explain(
    txn.validated,
    `This transaction ${
      txn.validated ? 'was' : 'was not'
    } in a validated ledger.`,
  )

  const account = explain(
    transactionInstructions.Account,
    `Account ${cffd(
      transactionInstructions.Account,
      'account',
    )} initiated this transaction.`,
  )

  const transactionType = explain(
    transactionInstructions.TransactionType,
    `This transaction was a ${cffd(
      transactionInstructions.TransactionType,
      'transactionType',
    )} transaction`,
  )

  const fee = explain(
    transactionInstructions.Fee,
    `This transaction destroyed ${cffd(
      transactionInstructions.Fee,
      'fee',
    )} XRP (in drops) as a fee.`,
  )

  // TODO: Will have to change this explanation when the "Tickets" feature is deployed to XRPL.
  const sequence = explain(
    transactionInstructions.Sequence,
    `This transaction has sequence number ${cffd(
      transactionInstructions.Sequence.toString(),
      'current-sequence-number',
    )} and is valid only if the last transaction made by this account had sequence number ${cffd(
      (transactionInstructions.Sequence - 1).toString(),
      'previous-sequence-number',
    )}`,
  )

  const accountTxnID = explainOptional(
    transactionInstructions.AccountTxnID,
    (txnId) =>
      `This transaction is only valid if the hash of the last transaction sent by this account has hash ${cffd(
        txnId,
        'accountTxnID',
      )}`,
  )

  const flags = explain(
    matchFlags(transactionInstructions.Flags, baseFlags),
    'A set of bit-wise flags for this transaction that contain additional transaction options.',
  )

  const lastLedgerSequence = explainOptional(
    transactionInstructions.LastLedgerSequence,
    (seq) =>
      `The last ledger this transaction can appear in is ledger ${cffd(
        seq.toString(),
        'lastLedgerSequence',
      )}`,
  )

  const memos = explainOptionalArray(
    transactionInstructions.Memos,
    createMemoExplanation,
    'This transaction had these memos:',
  )

  const signers = explainOptionalArray(
    transactionInstructions.Signers,
    createMultiSignatureExplanation,
    `This transaction had these signers:`,
  )

  const sourceTag = explainOptional(
    transactionInstructions.SourceTag,
    (tag) =>
      `This transaction has sourceTag ${cffd(
        tag.toString(),
        'sourceTag',
      )} to identify the reason for this payment, or a sender on whose behalf this transaction is made`,
  )

  const signingPubKey = explain(
    transactionInstructions.SigningPubKey,
    transactionInstructions.SigningPubKey === ''
      ? 'This transaction was multi-signed'
      : `This transaction was signed and the public key was ${cffd(
          transactionInstructions.SigningPubKey,
          'signingPubKey',
        )}.`,
  )

  const txnSignature = explain(
    transactionInstructions.TxnSignature,
    `This transaction was signed with signature ${cffd(
      transactionInstructions.TxnSignature,
      'txnSignature',
    )}.`,
  )

  return {
    type: txn.tx.TransactionType,

    hash,
    ...(date && { date }),
    ...(ledgerIndex && { ledgerIndex }),
    ...(ledgerHash && { ledgerHash }),
    meta,
    validated,

    account,
    transactionType,
    fee,
    sequence,
    ...(accountTxnID && { accountTxnID }),
    flags,
    ...(lastLedgerSequence && { lastLedgerSequence }),
    ...(memos && { memos }),
    ...(signers && { signers }),
    ...(sourceTag && { sourceTag }),
    signingPubKey,
    txnSignature,
  }
}
